/**
 * Attaching a contract or a ticket that already exists to this project.
 *
 * This dialog exists because nothing else in the CRM can make that link.
 * `Contract.projectId` and `Ticket.projectId` are loose string columns, the API
 * takes them on create and on PATCH, and both list routes filter on them - but
 * grep the contract section and the support section for `projectId` and neither
 * one comes back. There is no project field on the contract form and none on the
 * ticket form. So the two project tabs would otherwise list a relationship that
 * no screen in this app can create: an empty tab, a sentence pointing at a form,
 * and no such box on the form. That is a dead end, and the way out is here
 * rather than in a comment.
 *
 * ## Only records that belong to nobody are offered
 *
 * A candidate whose `projectId` is already set is filtered out, and the count of
 * what that hid is printed under the list. Offering a linked record would make
 * this dialog a way to quietly move a contract off somebody else's project - the
 * write is a PATCH of one column, so the old project loses the row with no
 * notice anywhere. Taking a record off a project is the unlink button on that
 * project's own row, where the person doing it can see what they are removing.
 *
 * The filter is local, over one page of results, which has a limit worth naming:
 * a search matching a hundred already-linked records shows an empty list while
 * free ones sit on page two. Hence the count line - an empty list with "already
 * on another project: 100" under it is a different message from an empty list on
 * its own, and the operator can narrow the search rather than conclude there is
 * nothing to link.
 */
import { useCallback, useEffect, useState } from "react";
import { Link2, Loader2, Unlink } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import { Btn, EmptyState, IconBtn, Modal, SearchField, StatusPill, useToast, type StatusTone } from "../../ui";

/**
 * The row control that takes a record back off this project.
 *
 * It lives beside the dialog rather than in either tab because it is the other
 * half of the same decision: attaching and detaching are one gesture, and the
 * two must stay in step about what they are gated on and how they look. It is
 * the ONLY piece the two tabs share - the rows themselves are drawn separately,
 * for the reason `ProjectInvoices` gives at length: a contract and a ticket have
 * almost nothing in common, and a shared row declaring the intersection would
 * throw away most of both.
 *
 * `can` false renders an empty cell rather than nothing at all, so the grid
 * keeps its last track and the columns above it stay where the head put them.
 */
export function UnlinkCell({
  can,
  busy,
  disabled,
  onClick,
}: {
  can: boolean;
  /** This row's own write is in flight. */
  busy: boolean;
  /** Any row's write is in flight - one detach at a time. */
  disabled: boolean;
  onClick: () => void;
}) {
  const t = useT();
  if (!can) return <span />;
  return (
    // `relative` so the button sits above the row link's stretched ::after,
    // which otherwise covers it and takes the click.
    <IconBtn
      small
      className="relative"
      disabled={disabled}
      onClick={onClick}
      aria-label={t("projects.unlinkFromProject")}
      title={t("projects.unlinkFromProject")}
    >
      {busy ? <Loader2 className="size-4 animate-spin" /> : <Unlink className="size-4" />}
    </IconBtn>
  );
}

/** One record this dialog can offer, flattened by the caller so the dialog never
 *  has to know whether it is looking at a contract or at a ticket. */
export interface LinkCandidate {
  id: string;
  /** The line a person recognises: a contract's title, a ticket's subject. */
  title: string;
  /** The record's own number, drawn under the title as the section lists draw it. */
  number: string;
  statusLabel: string;
  tone: StatusTone;
  /** The project this record already sits on. Null means it is free to link. */
  projectId: string | null;
}

export function LinkToProjectModal({
  title,
  searchPlaceholder,
  fetchCandidates,
  link,
  onClose,
  onLinked,
}: {
  title: string;
  searchPlaceholder: string;
  /** One page of candidates for the current search, already flattened. Must be
   *  stable (`useCallback`): it is a dependency of the effect that loads, so a
   *  fresh function on every render is a fetch on every render. */
  fetchCandidates: (q: string) => Promise<LinkCandidate[]>;
  /** Writes `projectId` onto the chosen record. */
  link: (id: string) => Promise<unknown>;
  onClose: () => void;
  onLinked: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");
  // Null is "still asking", which is not the same as an empty array. The two
  // states are drawn differently below and that difference is the whole point.
  const [rows, setRows] = useState<LinkCandidate[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    const id = setTimeout(() => setQ(qInput.trim()), 250);
    return () => clearTimeout(id);
  }, [qInput]);

  const load = useCallback(async () => {
    setRows(null);
    try {
      setRows(await fetchCandidates(q));
    } catch {
      // An empty list rather than a thrown render: the dialog still has to be
      // closable, and the toast says what happened.
      setRows([]);
      toast(t("common.error"));
    }
  }, [q, fetchCandidates, toast, t]);

  useEffect(() => {
    void load();
  }, [load]);

  async function pick(id: string) {
    setBusyId(id);
    try {
      await link(id);
      onLinked();
      onClose();
    } catch {
      toast(t("common.error"));
      setBusyId(null);
    }
  }

  const free = (rows ?? []).filter((r) => r.projectId === null);
  const taken = (rows ?? []).length - free.length;

  return (
    <Modal title={title} label={title} onClose={onClose}>
      <SearchField value={qInput} onChange={setQInput} placeholder={searchPlaceholder} autoFocus />
      <p className="crm-card-s mt-2">{t("projects.linkOnlyFree")}</p>

      <div className="mt-3 max-h-[46vh] min-h-[12rem] overflow-y-auto rounded-[22px] bg-[var(--crm-sunk)] p-2">
        {rows === null ? (
          <div className="flex justify-center py-12 text-muted-foreground">
            <Loader2 className="size-5 animate-spin" />
          </div>
        ) : free.length === 0 ? (
          <EmptyState icon={Link2} title={t("projects.linkNothingFree")} />
        ) : (
          <ul className="flex flex-col gap-1.5">
            {free.map((row) => (
              <li key={row.id} className="crm-prow gap-2 p-2.5">
                <button
                  type="button"
                  className="min-w-0 flex-1 text-left"
                  disabled={busyId !== null}
                  onClick={() => void pick(row.id)}
                >
                  <span className="crm-prow-n crm-trunc block">{row.title}</span>
                  <span className="crm-prow-s crm-tnum crm-trunc block">{row.number}</span>
                </button>
                <StatusPill tone={row.tone}>{row.statusLabel}</StatusPill>
                {busyId === row.id ? <Loader2 className="size-4 animate-spin" /> : null}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Printed whenever anything was hidden, including next to a full list:
          "there were more, and here is why you cannot see them" is worth saying
          before the operator concludes the search is broken. */}
      {taken > 0 ? <p className="crm-card-s mt-2">{t("projects.linkTaken", { n: taken })}</p> : null}

      <div className="crm-m-foot">
        <Btn type="button" onClick={onClose}>
          {t("common.close")}
        </Btn>
      </div>
    </Modal>
  );
}
